import React, { useMemo, useState } from 'react';
import {
  Grade,
  Subject,
  CognitiveDemand,
  GuidanceLevel,
  ContextLevel,
  AssessmentType,
  QuestionType,
  ArtifactType,
  Tenant,
  UserProfile,
} from '../types';
import { Dropdown } from './Dropdown';
import { FileUpload } from './FileUpload';
import {
  GRADES,
  SUBJECTS,
  COGNITIVE_DEMANDS,
  GUIDANCE_LEVELS,
  CONTEXT_LEVELS,
  ASSESSMENT_TYPES,
  QUESTION_TYPES,
} from '../constants';
import { runService } from '../services/runService';
import { generateSingleArtifactPdf } from '../utils/assessorPdfGenerator';
import { MarkBreakupInput, MarkBreakup } from './MarkBreakupInput';
import { extractTextFromPdf, countPdfPages } from '../utils/pdfUtils';
import { useAuth } from '../contexts/AuthContext';

type GenerateAssessmentOptions = Record<string, any>;

interface TheAssessorModuleProps {
  tenant: Tenant;
  userProfile: UserProfile;
  brandLogo: string;
  partnerLogo: string;
}

export interface AssessmentInputs {
  grade: Grade;
  subject: Subject;
  chapters: string;
  totalMarks: string;
  duration: string;
  assessmentType: AssessmentType;
}

type GenerationStatus = 'pending' | 'generating' | 'done' | 'error';

const ARTIFACT_ORDER: ArtifactType[] = [
  'questionPaper',
  'answerKey',
  'markingRubric',
  'assessmentBlueprint',
];

const ARTIFACT_TITLES: { [key in ArtifactType]: string } = {
  questionPaper: 'Question Paper',
  answerKey: 'Answer Key',
  markingRubric: 'Marking Rubric',
  assessmentBlueprint: 'Assessment Blueprint',
};

export const TheAssessorModule: React.FC<TheAssessorModuleProps> = ({
  tenant,
  userProfile,
  brandLogo,
  partnerLogo,
}) => {
  const [showInputForm, setShowInputForm] = useState<boolean>(false);

  // Form State
  const [grade, setGrade] = useState<Grade>(Grade.GRADE_8);
  const [subject, setSubject] = useState<Subject>(Subject.SOCIAL_STUDIES);
  const [assessmentType, setAssessmentType] = useState<AssessmentType>(AssessmentType.UNIT_TEST);
  const [questionType, setQuestionType] = useState<QuestionType>(QuestionType.MIXED);
  const [chapterFiles, setChapterFiles] = useState<File[]>([]);
  const [totalMarks, setTotalMarks] = useState<string>('20');
  const [duration, setDuration] = useState<string>('45');
  const [markBreakup, setMarkBreakup] = useState<MarkBreakup[]>([{ marks: '2', count: '10' }]);
  const [cognitiveDemand, setCognitiveDemand] = useState<CognitiveDemand>(CognitiveDemand.BALANCED);
  const [guidanceLevel, setGuidanceLevel] = useState<GuidanceLevel>(GuidanceLevel.MEDIUM);
  const [contextLevel, setContextLevel] = useState<ContextLevel>(ContextLevel.MIXED);

  // App Logic State
  const [error, setError] = useState<string | null>(null);
  const [isFormLocked, setIsFormLocked] = useState<boolean>(false);
  const [isCreditDeducted, setIsCreditDeducted] = useState<boolean>(false);
  const [extractedContent, setExtractedContent] = useState<string | null>(null);

  const [generatedContent, setGeneratedContent] = useState<Record<ArtifactType, string>>({
    questionPaper: '',
    answerKey: '',
    markingRubric: '',
    assessmentBlueprint: '',
  });

  const [generationStatus, setGenerationStatus] = useState<Record<ArtifactType, GenerationStatus>>({
    questionPaper: 'pending',
    answerKey: 'pending',
    markingRubric: 'pending',
    assessmentBlueprint: 'pending',
  });

  const { updateTenant } = useAuth();

  const chapterNames = useMemo(() => chapterFiles.map((f) => f.name).join(', '), [chapterFiles]);

  const computeInputPages = async (files: File[]): Promise<number> => {
    let pages = 0;
    for (const f of files) {
      const t = String(f?.type || '');
      if (t === 'application/pdf') pages += await countPdfPages(f);
      else if (t.startsWith('image/')) pages += 1;
      else if (t === 'text/plain') pages += 1;
      else pages += 1;
    }
    return pages;
  };

  const buildPreviousArtifacts = (artifactType: ArtifactType) => {
    const out: Partial<Record<ArtifactType, string>> = {};

    if (artifactType === 'answerKey') {
      out.questionPaper = generatedContent.questionPaper;
    }

    if (artifactType === 'markingRubric') {
      out.questionPaper = generatedContent.questionPaper;
      out.answerKey = generatedContent.answerKey;
    }

    if (artifactType === 'assessmentBlueprint') {
      out.questionPaper = generatedContent.questionPaper;
    }

    return out;
  };

  const handleGenerateArtifact = async (artifactType: ArtifactType) => {
    if (chapterFiles.length === 0 || !totalMarks.trim() || !duration.trim()) {
      setError('Please fill in all required fields and upload chapter content before starting.');
      return;
    }

    setError(null);
    setIsFormLocked(true);

    let contentToUse = extractedContent;

    // Step 1: Extract text once
    if (contentToUse === null) {
      setGenerationStatus((prev) => ({ ...prev, [artifactType]: 'generating' }));
      try {
        let combined = '';
        const pdfFiles = chapterFiles.filter((f) => f.type === 'application/pdf');

        for (const file of pdfFiles) {
          combined += `\n\n--- CONTENT FROM: ${file.name} ---\n${await extractTextFromPdf(file)}`;
        }

        contentToUse = combined.trim();
        setExtractedContent(contentToUse);
      } catch (err: any) {
        setError(`Failed during text extraction.\n${err?.message || ''}`);
        setGenerationStatus((prev) => ({ ...prev, [artifactType]: 'error' }));
        setIsFormLocked(false);
        return;
      }
    }

    // Step 2: Generate artifact
    setGenerationStatus((prev) => ({ ...prev, [artifactType]: 'generating' }));

    try {
      const markBreakupString = markBreakup
        .map((item) => `${item.count} question(s) of ${item.marks} marks each`)
        .join(', ');

      const inputPages = await computeInputPages(chapterFiles);

      const options: GenerateAssessmentOptions = {
        tenant,
        grade,
        subject,
        totalMarks,
        duration,
        assessmentType,
        questionType,
        markBreakup: markBreakupString,
        cognitiveDemand,
        guidanceLevel,
        contextLevel,
        artifactType,

        previousArtifacts: buildPreviousArtifacts(artifactType),

        // text-only path
        summarizedText: contentToUse,

        // billable page signal (do not rely on inputFiles for counting)
        inputPages,

        // keep empty to avoid uploading PDFs/images
        inputFiles: [],
      };

      const runResult = await runService.startRun(tenant, userProfile, 'assessor', options);
      const result = runResult?.aiResult || {};

      if (!isCreditDeducted && runResult?.newTenant) {
        updateTenant(runResult.newTenant);
        setIsCreditDeducted(true);
      }

      const artifactText = String((result as any)?.resultText || '').trim();
      setGeneratedContent((prev) => ({ ...prev, [artifactType]: artifactText }));

      setGenerationStatus((prev) => ({ ...prev, [artifactType]: 'done' }));
    } catch (err: any) {
      setError(
        `Generation failed for ${artifactType}:\n${err?.message || 'An unknown error occurred.'}`
      );
      setGenerationStatus((prev) => ({ ...prev, [artifactType]: 'error' }));
    }
  };

  const resetFormState = () => {
    setGrade(Grade.GRADE_8);
    setSubject(Subject.SOCIAL_STUDIES);
    setAssessmentType(AssessmentType.UNIT_TEST);
    setQuestionType(QuestionType.MIXED);
    setCognitiveDemand(CognitiveDemand.BALANCED);
    setGuidanceLevel(GuidanceLevel.MEDIUM);
    setContextLevel(ContextLevel.MIXED);
    setChapterFiles([]);
    setTotalMarks('20');
    setDuration('45');
    setMarkBreakup([{ marks: '2', count: '10' }]);
    setError(null);
    setIsFormLocked(false);
    setIsCreditDeducted(false);
    setExtractedContent(null);
    setGeneratedContent({
      questionPaper: '',
      answerKey: '',
      markingRubric: '',
      assessmentBlueprint: '',
    });
    setGenerationStatus({
      questionPaper: 'pending',
      answerKey: 'pending',
      markingRubric: 'pending',
      assessmentBlueprint: 'pending',
    });
  };

  const handleStartOver = () => {
    resetFormState();
    setShowInputForm(true);
  };

  const renderInitialView = () => (
    <div
      onClick={() => {
        resetFormState();
        setShowInputForm(true);
      }}
      className="flex-grow flex flex-col justify-center items-center text-center cursor-pointer hover:bg-gray-50 transition-colors duration-200 p-4 rounded-b-xl"
      role="button"
      aria-label="Open Assessment Creator Module"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-16 w-16 text-brand-orange mb-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
        />
      </svg>
      <p className="text-brand-grey text-lg mb-6 max-w-xs">
        Generate printable, curriculum-aligned assessments with model answers and rubrics.
      </p>
      <p className="text-brand-orange font-semibold mt-auto pt-4">Create an assessment →</p>
    </div>
  );

  const renderInputForm = () => (
    <>
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => {
            resetFormState();
            setShowInputForm(false);
          }}
          className="px-4 py-2 bg-gray-200 text-brand-grey text-sm font-semibold rounded-md shadow-sm hover:bg-gray-300"
        >
          ← Back
        </button>
        {isFormLocked && (
          <button
            onClick={handleStartOver}
            className="px-4 py-2 bg-brand-orange text-white text-sm font-semibold rounded-md shadow-sm hover:bg-opacity-90"
          >
            Start Over
          </button>
        )}
      </div>

      <fieldset disabled={isFormLocked} className={isFormLocked ? 'opacity-60' : ''}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Dropdown
            label="Grade"
            options={GRADES}
            selectedValue={grade}
            onValueChange={(v) => setGrade(v as Grade)}
            id="assessor-grade"
          />
          <Dropdown
            label="Subject"
            options={SUBJECTS}
            selectedValue={subject}
            onValueChange={(v) => setSubject(v as Subject)}
            id="assessor-subject"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Dropdown
            label="Assessment Type"
            options={ASSESSMENT_TYPES}
            selectedValue={assessmentType}
            onValueChange={(v) => setAssessmentType(v as AssessmentType)}
            id="assessor-atype"
          />
          <Dropdown
            label="Question Type Mix"
            options={QUESTION_TYPES}
            selectedValue={questionType}
            onValueChange={(v) => setQuestionType(v as QuestionType)}
            id="assessor-qtype"
          />
        </div>

        <FileUpload
          label="Chapter Content (PDF or Images)"
          accept=".pdf,image/*"
          onFileChange={setChapterFiles}
          multiple={true}
        />

        <div className="grid grid-cols-2 gap-4">
          <div className="mb-4">
            <label htmlFor="total-marks" className="block text-sm font-bold text-brand-grey mb-1">
              Total Marks
            </label>
            <input
              type="number"
              id="total-marks"
              value={totalMarks}
              onChange={(e) => setTotalMarks(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-brand-grey"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="duration" className="block text-sm font-bold text-brand-grey mb-1">
              Duration (minutes)
            </label>
            <input
              type="number"
              id="duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-brand-grey"
            />
          </div>
        </div>

        <MarkBreakupInput
          value={markBreakup}
          onChange={setMarkBreakup}
          totalMarks={parseInt(totalMarks, 10) || 0}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Dropdown
            label="Cognitive Demand"
            options={COGNITIVE_DEMANDS}
            selectedValue={cognitiveDemand}
            onValueChange={(v) => setCognitiveDemand(v as CognitiveDemand)}
            id="assessor-cog"
          />
          <Dropdown
            label="Guidance Level"
            options={GUIDANCE_LEVELS}
            selectedValue={guidanceLevel}
            onValueChange={(v) => setGuidanceLevel(v as GuidanceLevel)}
            id="assessor-guide"
          />
          <Dropdown
            label="Context Level"
            options={CONTEXT_LEVELS}
            selectedValue={contextLevel}
            onValueChange={(v) => setContextLevel(v as ContextLevel)}
            id="assessor-context"
          />
        </div>
      </fieldset>

      <div className="mt-8 border-t border-gray-200 pt-6 space-y-4">
        {error && (
          <pre className="whitespace-pre-wrap text-sm text-red-600 p-3 bg-red-50 rounded-md font-sans">
            {error}
          </pre>
        )}

        {ARTIFACT_ORDER.map((artifactType, index) => (
          <ArtifactGeneratorStep
            key={artifactType}
            title={ARTIFACT_TITLES[artifactType]}
            status={generationStatus[artifactType]}
            content={generatedContent[artifactType]}
            isLocked={index > 0 && generationStatus[ARTIFACT_ORDER[index - 1]] !== 'done'}
            onGenerate={() => handleGenerateArtifact(artifactType)}
            assessmentInputs={{
              grade,
              subject,
              chapters: chapterNames,
              totalMarks,
              duration,
              assessmentType,
            }}
            brandLogo={brandLogo}
            partnerLogo={partnerLogo}
          />
        ))}
      </div>
    </>
  );

  return (
    <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-8 md:p-10 lg:p-12 flex flex-col h-full transition-all duration-300 ease-in-out hover:shadow-2xl hover:-translate-y-2">
      <h2 className="text-3xl font-extrabold text-brand-grey mb-8 border-b-2 border-brand-orange pb-2">
        The <span className="text-brand-orange">Assessor</span>
        <sup className="text-sm">™</sup>
      </h2>
      <div className="flex-grow flex flex-col">{showInputForm ? renderInputForm() : renderInitialView()}</div>
    </div>
  );
};

interface ArtifactGeneratorStepProps {
  brandLogo: string;
  partnerLogo: string;
  tenantLogoUrl?: string;
  title: string;
  status: GenerationStatus;
  content: string;
  isLocked: boolean;
  onGenerate: () => void;
  assessmentInputs: AssessmentInputs;
}

const ArtifactGeneratorStep: React.FC<ArtifactGeneratorStepProps> = (props) => {
  const { title, status, content, isLocked, onGenerate, assessmentInputs, brandLogo, partnerLogo, tenantLogoUrl } =
    props;

  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2500);
  };

  const handleDownloadPdf = () => {
    generateSingleArtifactPdf(title, content, assessmentInputs, brandLogo, partnerLogo, tenantLogoUrl);
  };

  const statusIndicator = {
    pending: <div className="w-5 h-5 border-2 border-gray-400 rounded-full" title="Pending" />,
    generating: (
      <div
        className="w-5 h-5 border-2 border-brand-orange border-t-transparent rounded-full animate-spin"
        title="Generating..."
      />
    ),
    done: (
      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white" title="Done">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    ),
    error: (
      <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white" title="Error">
        <span className="font-bold text-xs">!</span>
      </div>
    ),
  }[status];

  return (
    <div className={`p-4 border rounded-lg transition-all ${isLocked ? 'bg-gray-50' : 'bg-white'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {statusIndicator}
          <h3 className={`text-lg font-semibold ${isLocked ? 'text-gray-400' : 'text-brand-grey'}`}>{title}</h3>
        </div>

        {status === 'pending' && (
          <button
            onClick={onGenerate}
            disabled={isLocked}
            className="px-4 py-2 bg-brand-orange text-white text-sm font-semibold rounded-md shadow-sm hover:bg-opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Generate
          </button>
        )}
      </div>

      {status === 'done' && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <pre className="whitespace-pre-wrap bg-gray-100 p-3 rounded text-sm font-sans max-h-48 overflow-y-auto">
            {content}
          </pre>
          <div className="flex justify-end items-center mt-3">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCopy}
                className={`text-xs px-3 py-1 rounded ${isCopied ? 'bg-green-600 text-white' : 'bg-gray-200 text-brand-grey'}`}
              >
                {isCopied ? 'Copied!' : 'Copy'}
              </button>
              <button onClick={handleDownloadPdf} className="text-xs px-3 py-1 bg-brand-orange text-white rounded">
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
